import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import { CustomsListResponse, RequestCustomsParams } from "./types";
import { exportCustomsBillApi, getLiteCustomsApi } from "./api";
import { AppThunk } from "../../store";
import { getErrorMessage } from "@helpers/get-error-message";

type LiteCustomsType = CustomsListResponse & {
  loading: boolean;
  error: string | null;
  success: string | null;
  currentParams: Partial<RequestCustomsParams> | null;
  exporting: {
    loading: boolean;
    error: string | null;
  };
};

const initialState: LiteCustomsType = {
  items: null,
  meta: null,
  loading: false,
  error: null,
  success: null,
  total: 0,
  page: 0,
  size: 0,
  currentParams: null,
  exporting: {
    loading: false,
    error: null,
  },
};

const liteCustoms = createSlice({
  name: "liteCustoms",
  initialState: initialState,
  reducers: {
    getLiteCustomsStart(state: LiteCustomsType) {
      state.loading = true;
      state.error = null;
    },
    getLiteCustomsSuccess(
      state: LiteCustomsType,
      action: PayloadAction<
        CustomsListResponse & { params?: RequestCustomsParams }
      >,
    ) {
      const { items, total, page, size, meta, params } = action.payload;
      state.items = items;
      state.meta = meta;
      state.total = total;
      state.page = page;
      state.size = size;
      state.loading = false;
      if (params) {
        const { page, size, ...exportParams } = params;
        state.currentParams = exportParams;
      }
    },
    getLiteCustomsFailure(
      state: LiteCustomsType,
      action: PayloadAction<string>,
    ) {
      state.loading = false;
      state.error = action.payload;
      state.items = null;
    },
    getExportFailure(state: LiteCustomsType, action: PayloadAction<string>) {
      state.error = action.payload;
      state.exporting.loading = false;
    },
    hideWarning(state: LiteCustomsType) {
      state.error = null;
    },
    cleanLiteCustoms(state: LiteCustomsType) {
      state.items = null;
    },
    startExport(state: LiteCustomsType) {
      state.exporting = {
        loading: true,
        error: null,
      };
    },
    successExport(state: LiteCustomsType) {
      state.exporting.loading = false;
      state.exporting.error = null;
    },
  },
});

export const {
  getLiteCustomsStart,
  hideWarning,
  cleanLiteCustoms,
  getLiteCustomsSuccess,
  getLiteCustomsFailure,
  getExportFailure,
  startExport,
  successExport,
} = liteCustoms.actions;

export default liteCustoms.reducer;

export const fetchLiteCustoms =
  (params: RequestCustomsParams): AppThunk =>
  async (dispatch) => {
    try {
      dispatch(getLiteCustomsStart());
      const response = await getLiteCustomsApi(params);
      dispatch(getLiteCustomsSuccess({ ...response, params }));
    } catch (e) {
      dispatch(getLiteCustomsFailure(getErrorMessage(e)));
    }
  };

export const exportCustomsBill =
  (params?: Partial<RequestCustomsParams>): AppThunk =>
  async (dispatch) => {
    try {
      dispatch(startExport());
      const { file, filename } = await exportCustomsBillApi(params);

      const url = window.URL.createObjectURL(file);
      const link = document.createElement("a");
      link.href = url;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      dispatch(successExport());
    } catch {
      dispatch(getExportFailure("Downloading failed. Try again later."));
    }
  };
